<template>
  <div class="sidebar box">
    <div class="control has-icons-left has-icons-right">
      <b-autocomplete
          rounded
          v-model="companyName"
          v-bind:data="companiesFiltered"
          v-bind:custom-formatter="companyNameFormatter"
          placeholder="Search for a company or ticker"
          icon="search"
          icon-pack="fas"
          clearable
          @select="option => (activeCompany = option)">
          <template slot="empty">No results found</template>
      </b-autocomplete>
    </div>
    <br>
    <div v-if="companies.loaded && screens.loaded && activeCompany">
      <div class="columns is-centered">
        <div class="column is-one-quarter has-text-centered">
          <span class="icon is-large">
            <i class="fas fa-building fa-3x"></i>
          </span>
        </div>
        <div class="column is-two-quarters">
          <p class="subtitle is-4">
            {{ activeCompany.name }}
            <span class="heading">
              {{ activeCompany.primaryIdentifier.symbol }}
            </span>
          </p>
          <p>
            <b>Exclusion Flags:</b> {{ flaggedTable(true).screens.filter((itm) => { return itm.exclusionFlagged }).length }}
            <br>
            <b>Inclusion Flags:</b>&nbsp; {{ flaggedTable(true).screens.filter((itm) => { return itm.inclusionFlagged }).length }}
          </p>
        </div>
      </div>

      <div>
        <section>
          <b-tabs>
            <b-tab-item label="All">
              <b-table
                  :data="flaggedTable(false).screens"
                  :default-sort="['threshold', 'desc']"
              >
                <b-table-column field="name" label="Screen" sortable v-slot="props">
                  <a href="#_" v-on:click="activateScreenDetailPane(props.row.screen, activeCompany)">{{ props.row.name }}</a>
                </b-table-column>
                <b-table-column
                  field="human_percentile"
                  label="Percentile"
                  sortable
                  numeric
                  v-slot="props"
                  >
                  <template v-if="props.row.isBinary">
                    -
                  </template>
                  <template v-else>
                    {{ ordinalSuffixOf(props.row.human_percentile) }}
                  </template>
                </b-table-column>
                <b-table-column
                  field="userPercentile"
                  label="Threshold"
                  sortable
                  numeric
                  v-slot="props"
                >
                  <template v-if="props.row.screen.userPercentile && !props.row.isBinary">
                    {{ ordinalSuffixOf(props.row.screen.userPercentile) }}
                  </template>
                  <template v-else>
                    -
                  </template>
                </b-table-column>
                <b-table-column field="anyFlag" label="Flagged" sortable v-slot="props">
                  <FlagIcon
                    v-bind:exclusion="props.row.exclusionFlagged"
                    v-bind:inclusion="props.row.inclusionFlagged"
                  />
                </b-table-column>
              </b-table>
            </b-tab-item>

            <b-tab-item label="Flagged">
              <b-table
                  :data="flaggedTable(true).screens"
              >
                <b-table-column field="name" label="Screen" sortable v-slot="props">
                  <a href="#_" v-on:click="$root.$emit('screen-selected', props.row.screen, activeCompany)">{{ props.row.name }}</a>
                </b-table-column>
                <b-table-column field="human_percentile" label="Percentile" sortable v-slot="props">
                  {{ props.row.human_percentile }}%
                </b-table-column>
                <b-table-column field="screen.userPercentile" label="Threshold" sortable v-slot="props">
                  <template v-if="props.row.screen.userPercentile">
                    {{ props.row.screen.userPercentile }}%
                  </template>
                  <template v-else>
                    -
                  </template>
                </b-table-column>
                <b-table-column field="anyFlagged" label="Flagged" sortable v-slot="props">
                  <FlagIcon
                    v-bind:exclusion="props.row.exclusionFlagged"
                    v-bind:inclusion="props.row.inclusionFlagged"
                  />
                </b-table-column>
              </b-table>
            </b-tab-item>
          </b-tabs>
        </section>
      </div>
    </div>
  </div>
</template>

<script>
import FlagIcon from '@frontend/shared/components/FlagIcon.vue';
import eventBus from '@frontend/shared/EventBus.js';
import {preciseRound} from '@frontend/shared/Util.js';
import {ordinal_suffix_of} from '@frontend/shared/Util.js';
import telemetry from '@frontend/api/Telemetry';

export default {
  data() {
    return {
      activeCompany: null,
      companyName: '',
    }
  },
  props: {
    companies: Object,
    screens: Object,
  },
  watch: {
    companies: function(newCompanies, oldCompanies) {
      if(newCompanies && newCompanies.a.length == 0) {
        this.activeCompany = newCompanies.a[0]
      }
      else {
        this.activeCompany = null
      }
    },
    companyName: function(newCompanyName, oldCompanyName) {
      if(newCompanyName) {
        telemetry.track('Searched for a Company', { name: newCompanyName });
      }
    },
  },
  components: {
    FlagIcon,
  },
  beforeUpdate() {
    if(this.activeCompany == null && this.companies && this.companies.a.length > 0) {
      this.activeCompany = this.companies.a[0]
    }
  },
  computed: {
    companiesFiltered: function() {
      return this.companies.a.filter((company) => {
          return this.companyName && this.companyNameFormatter(company)
              .toLowerCase()
              .indexOf(this.companyName.toLowerCase()) >= 0
      });
    },
  },
  methods: {
    activateScreenDetailPane: function(screen, company) {
      eventBus.$emit('reveal-screen-detail-pane', screen, company);
    },
    companyNameFormatter: function(company) {
      return `${company.name} (${company.primaryIdentifier.symbol})`
    },
    ordinalSuffixOf: function(i) {
      return ordinal_suffix_of(i)
    },
    flaggedTable: function(flaggedOnly) {
      let companyScreens = this.activeCompany.metrics.a
        .map((itm) => {
          const ret = {
            screen: itm.screen,
            isBinary: itm.screen?.is_binary,
            name: itm.screen.name,
            metric_description: itm.metric_description,
            exclusionFlagged: !itm.screen.is_inclusionary && (itm.percentile * 100) < itm.screen.userPercentile,
            inclusionFlagged: itm.screen.is_inclusionary && itm.screen.userPercentile > 0 && (itm.percentile * 100) >= itm.screen.userPercentile,
            human_percentile: preciseRound((itm.percentile * 100),0),
          }
          if (ret.inclusionFlagged || ret.exclusionFlagged) {
            ret.anyFlagged = true;
          }
          return ret;
        })
        .sort((a, b) => {
          if (a.anyFlagged && b.anyFlagged) {
            return a.name > b.name ? 1 : -1;
          }
          return a.anyFlagged ? -1 : 1;
        });
      if (flaggedOnly) {
        companyScreens = companyScreens.filter((i) => { return i.anyFlagged === true; });
      }
      return {
        screens: companyScreens,
        columns: [
          {field: 'name', label: 'Screen'},
          {field: 'human_percentile', label: 'Percentile'},
          {field: 'screen.userPercentile', label: 'Threshold'},
        ]
      }
    },
  },
}
</script>

<style>
.sidebar {
  margin-top: .75rem;
  grid-row: 2;
  grid-column: 2/3;
  padding: .75rem;
  width: 100%;
  min-height: 500px;
}
</style>
